<template>
  <div class="page-container">
    <div class="header-row">
      <h2 class="page-title">{{ $t('household.counterparties.title') }}</h2>
      <div class="header-row__actions">
        <div class="search-wrapper search-wrapper--inline">
          <LucideIcon :size="18" class="search-icon">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
          </LucideIcon>
          <input
            v-model="searchTerm"
            type="text"
            :placeholder="$t('household.counterparties.searchPlaceholder')"
            class="search-input"
          />
        </div>
        <button class="btn-primary" @click="openDialog()">
          <LucideIcon :size="16" class="mr-2">
            <path d="M5 12h14"/><path d="M12 5v14"/>
          </LucideIcon>
          {{ $t('household.counterparties.addBtn') }}
        </button>
      </div>
    </div>

    <!-- Фильтры -->
    <div class="filter-card">
      <select v-model="filterType" class="type-select">
        <option value="all">{{ $t('household.shared.allTypes') }}</option>
        <option value="dealer">{{ $t('household.counterparties.filter.suppliers') }}</option>
        <option value="seller">{{ $t('household.counterparties.filter.buyers') }}</option>
        <option value="dealer_seller">{{ $t('household.counterparties.filter.both') }}</option>
      </select>
    </div>

    <!-- Список карточек -->
    <div class="cards-grid">
      <div v-for="item in filteredCounterparties" :key="item.id" class="counterparty-card">
        <div class="card-top">
          <div class="card-icon-title">
            <div class="icon-box">
              <LucideIcon :size="24" strokeWidth="1.5" class="text-blue">
                <path d="M6 22V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>
              </LucideIcon>
            </div>
            <div class="info-group">
              <h3 class="name">{{ item.name }}</h3>
              <p class="inn">{{ $t('household.counterparties.binPrefix') }}: {{ item.inn }}</p>
            </div>
          </div>
          <div class="actions">
            <button class="action-btn edit" @click="openDialog(item)">
              <LucideIcon :size="16">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </LucideIcon>
            </button>
            <button class="action-btn delete" @click="handleDelete(item.id)">
              <LucideIcon :size="16">
                <path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
              </LucideIcon>
            </button>
          </div>
        </div>

        <div class="card-details">
          <div class="badge-row">
            <span :class="['badge', item.type]">
              {{ $t('household.counterparties.typeLabels.' + item.type) }}
            </span>
          </div>

          <div class="contact-info">
            <p class="contact-person">
              <LucideIcon :size="14" class="mr-1">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </LucideIcon>
              {{ item.contact }}
            </p>
            <p class="phone">{{ formatPhone(item.phone) }}</p>
          </div>

          <div class="address-footer">
            {{ item.address }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="filteredCounterparties.length === 0" class="empty-state">
      {{ $t('household.counterparties.notFound') }}
    </div>

    <!-- Диалог -->
    <div v-if="isDialogOpen" class="modal-overlay" @click.self="closeDialog">
      <div class="modal-content">
        <h3 class="modal-title">
          {{ editingItem ? $t('household.counterparties.dialogEditTitle') : $t('household.counterparties.dialogAddTitle') }}
        </h3>
        <form @submit.prevent="handleSubmit" class="modal-form">
          <div class="form-group">
            <label>{{ $t('household.counterparties.orgLabel') }}</label>
            <input v-model="formData.name" type="text" required :placeholder="$t('household.counterparties.orgPlaceholder')" />
          </div>

          <div class="form-group">
            <label>{{ $t('household.counterparties.typeLabel') }}</label>
            <select v-model="formData.type">
              <option value="dealer">{{ $t('household.counterparties.typeLabels.dealer') }}</option>
              <option value="seller">{{ $t('household.counterparties.typeLabels.seller') }}</option>
              <option value="dealer_seller">{{ $t('household.counterparties.typeLabels.dealer_seller') }}</option>
            </select>
          </div>

          <div class="form-group">
            <label>{{ $t('household.counterparties.binLabel') }}</label>
            <input v-model="formData.inn" type="text" required :placeholder="$t('household.counterparties.binPlaceholder')" />
          </div>

          <div class="form-group">
            <label>{{ $t('household.counterparties.addressLabel') }}</label>
            <input v-model="formData.address" type="text" required />
          </div>

          <div class="form-row">
            <div class="form-group flex-1">
              <label>{{ $t('household.counterparties.contactLabel') }}</label>
              <input v-model="formData.contact" type="text" required />
            </div>
            <div class="form-group flex-1">
              <label>{{ $t('household.counterparties.phoneLabel') }}</label>
              <input
                :value="formData.phone"
                @input="onPhoneInput"
                type="tel"
                required
                :placeholder="$t('household.counterparties.phonePlaceholder')"
              />
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="closeDialog">{{ $t('common.cancel') }}</button>
            <button type="submit" class="btn-primary-modal">{{ editingItem ? $t('common.save') : $t('common.add') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import LucideIcon from '@/components/common/LucideIcon.vue';
import { 
  getCounterparties, 
  createCounterparty, 
  updateCounterparty, 
  deleteCounterparty 
} from '@/api/household_counterparties';

export default {
  name: 'HouseholdCounterpartiesView',
  components: { LucideIcon },
  data() {
    return {
      counterparties: [],
      searchTerm: '',
      filterType: 'all',
      isDialogOpen: false,
      editingItem: null,
      formData: {
        name: '',
        type: 'supplier',
        inn: '',
        address: '',
        contact: '',
        phone: '',
      },
      typeLabels: {
        dealer: 'Поставщик',
        seller: 'Покупатель',
        dealer_seller: 'Пост. и Покуп.',
      }
    };
  },
  computed: {
    filteredCounterparties() {
      return this.counterparties.filter((item) => {
        const matchesSearch = (item.name || '').toLowerCase().includes(this.searchTerm.toLowerCase()) || 
                             (item.bin || item.inn || '').includes(this.searchTerm);
        const backendType = item.counterparty_type || item.type;
        const matchesType = this.filterType === 'all' || backendType === this.filterType;
        return matchesSearch && matchesType;
      });
    }
  },
  async created() {
    await this.loadCounterparties();
  },
  methods: {
    async loadCounterparties() {
      const res = await getCounterparties();
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      // Map backend fields to frontend expectations if necessary
      this.counterparties = data.map(item => ({
        ...item,
        type: item.counterparty_type || item.type,
        inn: item.bin || item.inn,
        contact: item.contact_person || item.contact,
        phone: item.phone || ''
      }));
    },
    openDialog(item = null) {
      if (item) {
        this.editingItem = item;
        this.formData = { ...item };
      } else {
        this.editingItem = null;
        this.formData = {
          name: '',
          type: 'dealer',
          inn: '',
          address: '',
          contact: '',
          phone: '',
        };
      }
      this.isDialogOpen = true;
    },
    closeDialog() {
      this.isDialogOpen = false;
    },
    onPhoneInput(e) {
      let val = e.target.value.replace(/\D/g, '');
      if (val.startsWith('7') || val.startsWith('8')) {
        val = val.substring(1);
      }
      val = val.substring(0, 10);
      
      let formatted = '+7 ';
      if (val.length > 0) {
        formatted += '(' + val.substring(0, 3);
      }
      if (val.length > 3) {
        formatted += ') ' + val.substring(3, 6);
      }
      if (val.length > 6) {
        formatted += '-' + val.substring(6, 8);
      }
      if (val.length > 8) {
        formatted += '-' + val.substring(8, 10);
      }
      
      this.formData.phone = formatted;
      e.target.value = formatted;
    },
    formatPhone(phone) {
      if (!phone) return '';
      let val = phone.replace(/\D/g, '');
      if (val.length === 11 && (val.startsWith('7') || val.startsWith('8'))) {
        val = val.substring(1);
      }
      if (val.length !== 10) return phone;
      return `+7 (${val.substring(0, 3)}) ${val.substring(3, 6)}-${val.substring(6, 8)}-${val.substring(8, 10)}`;
    },
    async handleSubmit() {
      const payload = {
        name: this.formData.name,
        counterparty_type: this.formData.type,
        bin: this.formData.inn,
        address: this.formData.address,
        contact_person: this.formData.contact,
        phone: this.formData.phone
      };

      if (this.editingItem) {
        await updateCounterparty(this.editingItem.id, payload);
      } else {
        await createCounterparty(payload);
      }
      await this.loadCounterparties();
      this.closeDialog();
    },
    async handleDelete(id) {
      if (confirm(this.$t('household.counterparties.confirmDelete'))) {
        await deleteCounterparty(id);
        await this.loadCounterparties();
      }
    }
  }
};
</script>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; gap: 16px; flex-wrap: wrap; }
.header-row__actions { display: inline-flex; align-items: center; gap: 12px; }
.search-wrapper--inline { width: 320px; max-width: 100%; flex: 0 0 auto; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }

.filter-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  align-items: center;
}

.search-wrapper { position: relative; flex: 1; max-width: 400px; }
.search-icon { position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.search-input {
  width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 2rem;
  padding: 0.75rem 1rem 0.75rem 3rem;
  color: #030229;
  font-size: 0.95rem;
  outline: none;
}
.search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

.type-select {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  color: #030229;
  outline: none;
  min-width: 150px;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.counterparty-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  transition: all 0.2s;
}
.counterparty-card:hover { transform: translateY(-2px); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }

.card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.25rem; }
.card-icon-title { display: flex; gap: 1rem; align-items: center; }

.icon-box {
  width: 3.5rem;
  height: 3.5rem;
  background: #eff6ff;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.text-blue { color: #3b82f6; }

.name { color: #030229; font-weight: 700; margin: 0; font-size: 1.1rem; }
.inn { color: #717182; font-size: 0.85rem; margin: 0.25rem 0 0; }

.actions { display: flex; gap: 0.5rem; }
.action-btn {
  background: #f8fafc;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  color: #3b82f6;
  transition: all 0.2s;
}
.action-btn.delete { color: #ef4444; }
.action-btn:hover { background: #f1f5f9; }

.card-details { border-top: 1px solid #f1f1f4; padding-top: 1.25rem; }
.badge-row { margin-bottom: 1rem; }
.badge {
  display: inline-flex;
  padding: 0.35rem 0.75rem;
  border-radius: 2rem;
  font-size: 0.7rem;
  font-weight: 600;
}
.badge.dealer { background: #dcfce7; color: #15803d; }
.badge.seller { background: #dbeafe; color: #1d4ed8; }
.badge.dealer_seller { background: #fef9c3; color: #a16207; }

.contact-info { margin-bottom: 1rem; color: #4b5563; }
.contact-person { display: flex; align-items: center; font-weight: 500; margin-bottom: 0.25rem; }
.phone { font-size: 0.9rem; }

.address-footer {
  font-size: 0.85rem;
  color: #717182;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.empty-state { text-align: center; padding: 4rem; color: #717182; background: white; border-radius: 1rem; border: 1px dashed #e2e8f0; }

.btn-primary {
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s;
}
.btn-primary:hover { background: #1d4ed8; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: #ffffff;
  border-radius: 1.25rem;
  width: 100%;
  max-width: 550px;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}
.modal-title { color: #030229; font-size: 1.5rem; font-weight: 700; margin-bottom: 2rem; }

.form-group { margin-bottom: 1.25rem; }
.form-group label { display: block; color: #4b5563; font-size: 0.875rem; font-weight: 500; margin-bottom: 0.5rem; }
.form-group input, .form-group select {
  width: 100%;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  color: #111827;
  outline: none;
}
.form-group input:focus { border-color: #3b82f6; background: white; }

.modal-actions { display: flex; gap: 1rem; margin-top: 2.5rem; }
.btn-secondary {
  flex: 1;
  background: #f3f4f6;
  color: #4b5563;
  border: none;
  border-radius: 0.75rem;
  padding: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
.btn-secondary:hover { background: #e5e7eb; }

.btn-primary-modal {
  flex: 1;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
.btn-primary-modal:hover { background: #1d4ed8; }
.mr-1 { margin-right: 0.25rem; }
.mr-2 { margin-right: 0.5rem; }
</style>
```
